import { Task, WayPoint } from '@bringg/types';
import { Notification } from '@bringg/react-components';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { getSharedRootStore } from '@bringg-frontend/global-stores';
import { areTasksInSameDay } from '@bringg-frontend/shared-tasks-date';
import { getDropOffWayPoint } from '@bringg-frontend/utils';
import { useMonitoringTranslations } from '@bringg-frontend/monitoring';

import { loadRouteGroups } from '../shared/data/group';

const useRoutesPlannerManualValidation = (selectMany: (tasks: Task[], isSelected: boolean) => void) => {
	const {
		onlyTasksWithTeamsError,
		onlyOneTeamError,
		taskWithoutDropOffWayPointError,
		sameExecuteStatusError,
		noRouteSelectedError,
		onlyTasksWithRunError,
		sameDateError,
		openPreviewDescription
	} = useMonitoringTranslations();

	const getTeamsError = (selectedTasks: Task[]): string => {
		const teamIds = new Set<number>();
		selectedTasks.forEach(task => {
			teamIds.add(task.team_ids[0]);
		});

		if (teamIds.has(undefined)) {
			return onlyTasksWithTeamsError;
		}
		if (teamIds.size > 1) {
			return onlyOneTeamError;
		}
		return '';
	};

	const getTasksDropOffWayPoints = (selectedTasks: Task[]): WayPoint[] => {
		const dropOffWayPoints = [];
		selectedTasks.forEach(task => {
			const wp = getDropOffWayPoint(task);
			if (wp) {
				dropOffWayPoints.push(wp);
			}
		});
		return dropOffWayPoints;
	};
	const getWayPointError = (dropOffWayPoints: WayPoint[], selectedTasksLength: number): string => {
		if (dropOffWayPoints.length === 0 || dropOffWayPoints.length !== selectedTasksLength) {
			return taskWithoutDropOffWayPointError;
		}
	};

	const getTasksInSameExecuteStatusError = (selectedTasks: Task[]): string => {
		const firstTaskStatus = selectedTasks[0].ready_to_execute;
		const taksInDifferentStatus = selectedTasks.some(task => task.ready_to_execute !== firstTaskStatus);

		if (taksInDifferentStatus) {
			return sameExecuteStatusError;
		}
		return '';
	};

	const getRoutesPlannerManuallyOpenError = (selectedTasks: Task[]): string => {
		if (selectedTasks.length === 0) {
			return noRouteSelectedError;
		}
		const sameStatusError = getTasksInSameExecuteStatusError(selectedTasks);
		if (sameStatusError) {
			return sameStatusError;
		}

		const teamsError = getTeamsError(selectedTasks);
		if (teamsError) {
			return teamsError;
		}
		const dropOffWayPoints = getTasksDropOffWayPoints(selectedTasks);
		const WayPointError = getWayPointError(dropOffWayPoints, selectedTasks.length);
		if (WayPointError) {
			return WayPointError;
		}

		const taskWithRun = selectedTasks.find(task => task.run_id);
		if (!taskWithRun) {
			return onlyTasksWithRunError;
		}
		if (
			!getSharedRootStore().data.merchantConfigurationsStore.configuration.allow_multi_day_optimization &&
			!(
				areTasksInSameDay(dropOffWayPoints, 'no_earlier_than', 'scheduled_at') ||
				areTasksInSameDay(dropOffWayPoints, 'scheduled_at', 'no_earlier_than')
			)
		) {
			return sameDateError;
		}

		return '';
	};

	const addRouteUnselectedTasks = async (selectedTasks: Task[]) => {
		const selectedRoutesIds = new Set<number>();
		selectedTasks.forEach(task => {
			if (task.run_id) {
				selectedRoutesIds.add(task.run_id);
			}
		});

		const routesTasks = await loadRouteGroups([...selectedRoutesIds], selectedTasks[0].team_ids[0]);
		if (routesTasks.total === selectedTasks.length) {
			return false;
		}
		const tasksIds = Object.values(routesTasks.items_by_group)
			.flat()
			.map(task => task.id);

		const selectedTasksByIds: { [taskId: string]: Task } = {};
		const tasksToAdd: Task[] = [];
		selectedTasks.forEach(task => (selectedTasksByIds[task.id] = task));

		const tasks = await getRootEnv().dashboardSdk.sdk.v2.tasks.loadMany(tasksIds);
		tasks.forEach(task => {
			if (!selectedTasksByIds[task.id]) {
				tasksToAdd.push(task);
			}
		});
		Notification.info(openPreviewDescription);
		selectMany(tasksToAdd, true);
	};

	return { getRoutesPlannerManuallyOpenError, addRouteUnselectedTasks };
};

export default useRoutesPlannerManualValidation;
